import { Select, Typography } from 'antd';
import { type FC, useMemo } from 'react';
import type { SelectProps } from 'antd/lib/select';
import type { SerializedManufacturer, SerializedMeterModel, OutputTechnology } from '@enmon/commons/lib/tools';
import { useMeterModels } from 'api/hooks/meter-models/useMeterModels';
import { useManufacturerFamily } from 'api/hooks/manufacturers/useManufacturerFamilyIds';
import type { Commodity } from '@enmon/commons/lib/shared';
import { filterOption, filterSort } from '../utils';
import { mapListToIdSelectOptions } from '../../../utils/mapListToIdSelectOptions';
import { isNil } from 'ramda';

type MeterModelId = SerializedMeterModel['id'] | null;

export interface MeterModelsSelectProps extends SelectProps<MeterModelId> {
  filter?: {
    commodity?: Commodity;
    manufacturerId?: SerializedManufacturer['id'];
    technologies?: OutputTechnology[];
    meterModelId?: SerializedMeterModel['id'];
  };
  onChange?: (value: MeterModelId) => void;
  onModelSelect?: (model: SerializedMeterModel | null) => void;
  onModelManufacturerChange?: (manufacturerId: SerializedManufacturer['id']) => void;
  onSuccess?: () => void;
}

const UNKNOWN_MANUFACTURER = 'Unknown Manufacturer';

export const MeterModelsSelect: FC<MeterModelsSelectProps> = props => {
  const { filter, onChange, onModelSelect, onModelManufacturerChange, onSuccess, ...propsToPass } = props;
  const { commodity, manufacturerId, technologies } = filter ?? {};

  const { members: family, isLoading: isLoadingFamily } = useManufacturerFamily(manufacturerId);
  const isFamilyExtended = family.length > 1;

  const { data: meterModels, isFetching: isLoadingMeterModels } = useMeterModels(
    {
      ...(commodity && { commodity }),
      ...(manufacturerId && { manufacturerId }),
      ...(technologies && { technologies }),
      populate: { manufacturer: { fields: ['id', 'name'] } },
      fields: ['id', 'name', 'manufacturer', 'folder_drive_id'],
      limit: -1,
    },
    undefined,
    { includeManufacturerFamily: true },
  );

  const modelsById = useMemo(() => new Map((meterModels?.data ?? []).map(model => [model.id, model])), [meterModels]);

  const manufacturersOptions = useMemo(() => {
    if (!meterModels?.data) return [];

    const grouped = new Map<string, { label: string; isPicked: boolean; models: SerializedMeterModel[] }>();

    for (const model of meterModels.data) {
      const key = String(model.manufacturer?.id ?? UNKNOWN_MANUFACTURER);

      const group = grouped.get(key) ?? {
        label: model.manufacturer?.name ?? UNKNOWN_MANUFACTURER,
        isPicked: model.manufacturer?.id === manufacturerId,
        models: [],
      };

      group.models.push(model);
      grouped.set(key, group);
    }

    // The picked manufacturer's own models first, the rest of its family after them.
    return [...grouped.values()]
      .sort((a, b) => Number(b.isPicked) - Number(a.isPicked) || a.label.localeCompare(b.label))
      .map(({ label, models }) => ({ label, options: mapListToIdSelectOptions(models) }));
  }, [meterModels, manufacturerId]);

  const handleChange = (value: MeterModelId) => {
    onChange?.(value);
    const model = !isNil(value) ? (modelsById.get(value) ?? null) : null;
    onModelSelect?.(model);

    const modelManufacturerId = model?.manufacturer?.id;
    if (manufacturerId && modelManufacturerId && modelManufacturerId !== manufacturerId) {
      onModelManufacturerChange?.(modelManufacturerId);
    }
  };

  return (
    <Select
      allowClear
      showSearch={{
        filterOption,
        filterSort,
        optionFilterProp: 'label',
      }}
      loading={isLoadingMeterModels || isLoadingFamily}
      options={manufacturersOptions}
      placeholder="Select Meter Model"
      popupMatchSelectWidth={false}
      onChange={handleChange}
      popupRender={menu =>
        isFamilyExtended ? (
          <>
            <Typography.Text type="secondary" style={{ display: 'block', padding: '4px 12px 8px' }}>
              Includes models of the whole manufacturer family: {family.map(({ name }) => name).join(', ')}
            </Typography.Text>
            {menu}
          </>
        ) : (
          menu
        )
      }
      {...propsToPass}
    />
  );
};
